// Surface BOP Test Report — record-support only.
// Never certifies pass/fail, BOP condition, fitness-for-service, well safety,
// shear capability, casing protection, or accumulator/closing-unit readiness.
// User-entered values only. Missing fields → "Not entered."
import { polishProse, MISSING } from "@/lib/ddr/templates";

const orMissing = (v) => (v != null && String(v).trim() ? String(v).trim() : MISSING);
const polishedOrMissing = (v) => polishProse(v) || MISSING;
const yn = (v) => (v === true || v === "yes" || v === "Yes" ? "Yes" : v === false || v === "no" || v === "No" ? "No" : v ? String(v) : MISSING);

const sec = (lines, label) => lines.push(`\n=== ${label.toUpperCase()} ===\n`);

export function buildSurfaceBopTestReport(state) {
  const h = state.header || {};
  const stack = state.stack || {};
  const scope = state.scope || {};
  const components = Array.isArray(state.components) ? state.components : [];
  const annular = state.annular || {};
  const ram = state.ram || {};
  const blind = state.blind || {};
  const choke = state.choke || {};
  const manifold = state.manifold || {};
  const accum = state.accum || {};
  const plug = state.plug || {};
  const trip = state.trip || {};
  const summary = state.summary || {};
  const obs = state.obs || {};
  const witness = state.witness || {};
  const notes = state.notes || {};
  const lines = [];
  const push = (s) => lines.push(s);

  // 1. Report Header
  sec(lines, "Report Header");
  push(`Report Date: ${orMissing(h.reportDate)}  ·  Report #: ${orMissing(h.reportNumber)}`);
  push(`Rig: ${orMissing(h.rigName)}  ·  Contractor: ${orMissing(h.contractor)}`);
  push(`Operator / Client: ${orMissing(h.operator)}`);
  push(`Well: ${orMissing(h.wellName)}  ·  Field / Block: ${orMissing(h.fieldBlock)}`);
  push(`Location: ${orMissing(h.location)}  ·  Country / Region: ${orMissing(h.country)}`);
  push(`Prepared By: ${orMissing(h.preparedBy)}  ·  Position: ${orMissing(h.position)}`);
  push(`Tour / Shift: ${orMissing(h.tour)}`);
  push(`Period Start: ${orMissing(h.periodStart)}  ·  Period End: ${orMissing(h.periodEnd)}`);
  push(`Unit System (user-entered): ${orMissing(h.unitSystem)}`);
  push(`Test Procedure Reference: ${orMissing(h.procedureRef)}`);
  push(`Client / Operator Representative: ${orMissing(h.clientRep)}`);
  push(`Drilling Supervisor / Company Rep: ${orMissing(h.companyRep)}`);
  push(`Toolpusher / Rig Manager: ${orMissing(h.toolpusher)}`);
  push(`BOP Technician: ${orMissing(h.bopTech)}`);
  push(`Mechanic / Electrician (if applicable): ${orMissing(h.mechElec)}`);

  // 2. Surface Stack Information
  sec(lines, "Surface Stack Information");
  push(`Surface Stack Type: ${orMissing(stack.stackType)}`);
  push(`Stack Configuration: ${polishedOrMissing(stack.configuration)}`);
  push(`Stack Rated Working Pressure: ${orMissing(stack.ratedWP)}`);
  push(`Wellhead / Casinghead Type: ${orMissing(stack.wellheadType)}`);
  push(`Wellhead Pressure Rating: ${orMissing(stack.wellheadRating)}`);
  push(`Casing Size: ${orMissing(stack.casingSize)}  ·  Grade / Weight: ${orMissing(stack.casingGrade)}`);
  push(`Casing Shoe Depth MD: ${orMissing(stack.shoeMd)}  ·  TVD: ${orMissing(stack.shoeTvd)}`);
  push(`Test Plug Used: ${yn(stack.testPlugUsed)}  ·  Cup Tester Used: ${yn(stack.cupTesterUsed)}`);
  push(`Test Plug / Cup Tester Details: ${polishedOrMissing(stack.testToolDetails)}`);
  push(`Stack Installed Date: ${orMissing(stack.installedDate)}`);
  push(`Last Full BOP Test Date: ${orMissing(stack.lastFullTest)}  ·  Last Function Test: ${orMissing(stack.lastFunctionTest)}`);
  push(`Stack Notes: ${polishedOrMissing(stack.notes)}`);

  // 3. Test Scope
  sec(lines, "Test Scope");
  push(`Test Type: ${orMissing(scope.testType)}`);
  push(`Test Reason: ${polishedOrMissing(scope.testReason)}`);
  push(`Test Medium: ${orMissing(scope.testMedium)}  ·  Test Pump: ${orMissing(scope.testPump)}`);
  push(`Test Line Used: ${orMissing(scope.testLine)}`);
  push(`Chart / Recorder Reference: ${orMissing(scope.chartRef)}`);
  push(`Digital Recording Reference: ${orMissing(scope.digitalRef)}`);
  push(`Test Started: ${orMissing(scope.startTime)}  ·  Completed: ${orMissing(scope.endTime)}`);
  push(`Test Status (user-entered only): ${orMissing(scope.userStatus)}`);
  push(`Rough Notes: ${polishedOrMissing(scope.roughNotes)}`);

  // 4. Surface BOP Components Tested
  sec(lines, "Surface BOP Components Tested");
  if (!components.length) {
    push("No component rows entered. Add a row to record each BOP component tested.");
  } else {
    components.forEach((c, i) => {
      const idx = i + 1;
      push(`-- Component ${idx} --`);
      push(`Name: ${orMissing(c.name)}  ·  Type: ${orMissing(c.componentType)}`);
      push(`Open / Close Function Recorded: ${yn(c.fnRecorded)}`);
      push(`Low-Pressure Test (user-entered): ${orMissing(c.lowP)}  ·  High-Pressure Test: ${orMissing(c.highP)}`);
      push(`Hold Period: ${orMissing(c.hold)}  ·  Pressure Drop Observed: ${orMissing(c.pressureDrop)}`);
      push(`Bleed-Off Observed: ${orMissing(c.bleedOff)}`);
      push(`Leak Observed (user-entered): ${yn(c.leak)}`);
      push(`Status (user-entered, not classified by tool): ${orMissing(c.status)}`);
      push(`Remarks: ${polishedOrMissing(c.remarks)}`);
    });
  }
  push("");
  push("Component test records are recorded as user-entered observations only. This tool does not determine pass / fail and does not classify any component as serviceable.");

  // 5. Annular Preventer Test Record
  sec(lines, "Annular Preventer Test Record");
  push(`Annular Tested: ${yn(annular.tested)}`);
  push(`Test Pipe Size: ${orMissing(annular.pipeSize)}`);
  push(`Low Test Pressure: ${orMissing(annular.lowP)}  ·  High Test Pressure: ${orMissing(annular.highP)}`);
  push(`Hold Duration: ${orMissing(annular.hold)}`);
  push(`Closing Pressure: ${orMissing(annular.closingP)}  ·  Opening Pressure: ${orMissing(annular.openingP)}`);
  push(`Regulator Setting: ${orMissing(annular.regulator)}`);
  push(`Pressure Drop Observed: ${orMissing(annular.pressureDrop)}`);
  push(`Leak Observed (user-entered): ${yn(annular.leak)}`);
  push(`Status (user-entered): ${orMissing(annular.status)}`);
  push(`Remarks: ${polishedOrMissing(annular.remarks)}`);

  // 6. Ram Preventer Test Record
  sec(lines, "Ram Preventer Test Record");
  push(`Ram Type Tested: ${orMissing(ram.ramType)}`);
  push(`Pipe Size / Test Pipe: ${orMissing(ram.pipeSize)}`);
  push(`Ram Cavity / Position: ${orMissing(ram.cavity)}`);
  push(`Low-Pressure Test: ${orMissing(ram.lowP)}  ·  High-Pressure Test: ${orMissing(ram.highP)}`);
  push(`Hold Duration: ${orMissing(ram.hold)}`);
  push(`Closing Pressure: ${orMissing(ram.closingP)}  ·  Opening Pressure: ${orMissing(ram.openingP)}`);
  push(`Pressure Drop Observed: ${orMissing(ram.pressureDrop)}`);
  push(`Leak Observed (user-entered): ${yn(ram.leak)}`);
  push(`Status (user-entered): ${orMissing(ram.status)}`);
  push(`Remarks: ${polishedOrMissing(ram.remarks)}`);

  // 7. Blind Ram / Shear Ram Record
  sec(lines, "Blind Ram / Shear Ram Record");
  push(`Blind Ram Tested: ${yn(blind.blindTested)}`);
  push(`Blind Shear Ram Tested: ${yn(blind.shearTested)}`);
  push(`Function Test Only: ${yn(blind.functionOnly)}`);
  push(`Pressure Test Performed: ${yn(blind.pressureTested)}`);
  push(`Test Pressure: ${orMissing(blind.testP)}  ·  Hold Duration: ${orMissing(blind.hold)}`);
  push(`Closing Pressure: ${orMissing(blind.closingP)}  ·  Opening Pressure: ${orMissing(blind.openingP)}`);
  push(`Shear Function Test Performed (user-entered): ${yn(blind.shearPerformed)}`);
  push(`Test Object / Pipe Details: ${polishedOrMissing(blind.testObject)}`);
  push(`Status (user-entered): ${orMissing(blind.status)}`);
  push(`Remarks: ${polishedOrMissing(blind.remarks)}`);
  push("");
  push("This tool does not certify shear capability. Only user-entered test record information is shown.");

  // 8. Choke / Kill Line Test Record
  sec(lines, "Choke / Kill Line Test Record");
  push(`Choke Line Tested: ${yn(choke.chokeTested)}  ·  Kill Line Tested: ${yn(choke.killTested)}`);
  push(`Choke Line Test Pressure: ${orMissing(choke.chokeP)}  ·  Kill Line Test Pressure: ${orMissing(choke.killP)}`);
  push(`Hold Duration: ${orMissing(choke.hold)}`);
  push(`Pressure Drop Observed: ${orMissing(choke.pressureDrop)}`);
  push(`Leak Observed (user-entered): ${yn(choke.leak)}`);
  push(`Choke Line Volume: ${orMissing(choke.chokeVol)}  ·  Kill Line Volume: ${orMissing(choke.killVol)}`);
  push(`Test Direction / Flow Path: ${orMissing(choke.flowPath)}`);
  push(`Status (user-entered): ${orMissing(choke.status)}`);
  push(`Remarks: ${polishedOrMissing(choke.remarks)}`);

  // 9. Choke Manifold / Valves
  sec(lines, "Choke Manifold / Valve Record");
  push(`Choke Manifold Tested: ${yn(manifold.manifoldTested)}`);
  push(`Adjustable Choke Tested: ${yn(manifold.adjChokeTested)}  ·  Manual Choke Tested: ${yn(manifold.manualChokeTested)}`);
  push(`HCR Valves Tested: ${yn(manifold.hcrTested)}  ·  Manual Valves Tested: ${yn(manifold.manualTested)}  ·  Remote Valves Tested: ${yn(manifold.remoteTested)}`);
  push(`Test Pressure: ${orMissing(manifold.testP)}  ·  Hold Duration: ${orMissing(manifold.hold)}`);
  push(`Pressure Drop Observed: ${orMissing(manifold.pressureDrop)}`);
  push(`Leak Observed (user-entered): ${yn(manifold.leak)}`);
  push(`Status (user-entered): ${orMissing(manifold.status)}`);
  push(`Remarks: ${polishedOrMissing(manifold.remarks)}`);

  // 10. Accumulator / Closing Unit / Control System
  sec(lines, "Accumulator / Closing Unit Record");
  push(`Accumulator Unit Status (user-entered): ${orMissing(accum.unitStatus)}`);
  push(`Accumulator Pressure: ${orMissing(accum.pressure)}  ·  Pre-Charge Pressure: ${orMissing(accum.preCharge)}`);
  push(`Bottle Bank Status: ${orMissing(accum.bottleStatus)}`);
  push(`Usable Fluid Volume (user-entered): ${orMissing(accum.usableVol)}`);
  push(`Closing Unit Status (user-entered): ${orMissing(accum.closingUnit)}`);
  push(`Charge Pumps Status (user-entered): ${orMissing(accum.chargePumps)}`);
  push(`Air / Electric Pump Status: ${orMissing(accum.airElecPump)}`);
  push(`Regulator Settings: ${orMissing(accum.regulators)}`);
  push(`Manifold Pressure: ${orMissing(accum.manifoldPressure)}`);
  push(`Annular Regulator Pressure: ${orMissing(accum.annularRegPressure)}`);
  push(`Hydraulic Leaks Observed (user-entered): ${yn(accum.leaks)}`);
  push(`Function Test Notes: ${polishedOrMissing(accum.fnNotes)}`);
  push(`Remote Panel Status (user-entered): ${orMissing(accum.remotePanel)}`);
  push(`Driller's Panel Status (user-entered): ${orMissing(accum.drillersPanel)}`);
  push(`Koomey Unit Notes (if applicable): ${polishedOrMissing(accum.koomeyNotes)}`);
  push(`Status (user-entered): ${orMissing(accum.status)}`);
  push(`Remarks: ${polishedOrMissing(accum.remarks)}`);
  push("");
  push("This tool does not certify accumulator capacity, closing-unit readiness, or regulatory compliance.");

  // 11. Test Plug / Cup Tester / Casing Protection Record
  sec(lines, "Test Plug / Cup Tester Record");
  push(`Test Plug Used: ${yn(plug.testPlugUsed)}`);
  push(`Test Plug Type: ${orMissing(plug.testPlugType)}`);
  push(`Test Plug Setting Depth: ${orMissing(plug.testPlugDepth)}`);
  push(`Test Plug Rating: ${orMissing(plug.testPlugRating)}`);
  push(`Cup Tester Used: ${yn(plug.cupTesterUsed)}  ·  Cup Tester Size: ${orMissing(plug.cupTesterSize)}`);
  push(`Casing Protected (user-entered): ${yn(plug.casingProtected)}`);
  push(`Casing Pressure Limit: ${orMissing(plug.casingPressureLimit)}`);
  push(`Test Pressure Limit: ${orMissing(plug.testPressureLimit)}`);
  push(`Pressure Below Casing Limit (user-entered): ${yn(plug.belowCasingLimit)}`);
  push(`Remarks: ${polishedOrMissing(plug.remarks)}`);
  push("");
  push("This tool does not certify casing protection or pressure-limit compliance. Values are user-entered references only.");

  // 12. Trip Tank / Flow Check / Well Monitoring Record
  sec(lines, "Trip Tank / Flow Check Record");
  push(`Trip Tank Status: ${orMissing(trip.tankStatus)}`);
  push(`Trip Tank Volume Before Test: ${orMissing(trip.volBefore)}`);
  push(`Trip Tank Volume After Test: ${orMissing(trip.volAfter)}`);
  push(`Flow Check Performed: ${yn(trip.flowCheckPerformed)}`);
  push(`Flow Check Result (user-entered text only): ${polishedOrMissing(trip.flowCheckResult)}`);
  push(`Well Shut In (user-entered): ${yn(trip.wellShutIn)}`);
  push(`Well Monitoring Notes: ${polishedOrMissing(trip.monitoringNotes)}`);
  push(`Returns Observed: ${polishedOrMissing(trip.returnsObserved)}`);
  push(`Losses Observed: ${polishedOrMissing(trip.lossesObserved)}`);
  push(`Remarks: ${polishedOrMissing(trip.remarks)}`);
  push("");
  push("Flow-check and well-monitoring entries are recorded as user-entered observations. This tool does not declare the well static or safe.");

  // 13. Pressure Test Summary
  sec(lines, "Pressure Test Summary");
  push(`Test Pressure Units: ${orMissing(summary.units)}`);
  push(`Lowest Recorded Pressure: ${orMissing(summary.lowest)}`);
  push(`Highest Recorded Pressure: ${orMissing(summary.highest)}`);
  push(`Total Test Duration: ${orMissing(summary.totalDuration)}`);
  push(`Pressure Chart Attached / Reference: ${orMissing(summary.chartAttached)}`);
  push(`Bleed-Off Volume: ${orMissing(summary.bleedOffVol)}`);
  push(`Leaks Observed (user-entered): ${yn(summary.leaks)}`);
  push(`Retest Required (user-entered): ${yn(summary.retestRequired)}`);
  push(`Components Retested: ${polishedOrMissing(summary.componentsRetested)}`);
  push(`Final User-Entered Test Status: ${orMissing(summary.finalStatus)}`);
  push(`Remarks: ${polishedOrMissing(summary.remarks)}`);

  // 14. Observations / Exceptions / Follow-Up
  sec(lines, "Observations / Exceptions / Follow-Up");
  push(`Observations: ${polishedOrMissing(obs.observations)}`);
  push(`Exceptions: ${polishedOrMissing(obs.exceptions)}`);
  push(`Leaks Noted: ${polishedOrMissing(obs.leaksNoted)}`);
  push(`Equipment Issues: ${polishedOrMissing(obs.equipmentIssues)}`);
  push(`Retests Required: ${polishedOrMissing(obs.retestsRequired)}`);
  push(`Repairs Completed: ${polishedOrMissing(obs.repairsCompleted)}`);
  push(`Repairs Pending: ${polishedOrMissing(obs.repairsPending)}`);
  push(`Follow-Up Required: ${polishedOrMissing(obs.followUpRequired)}`);
  push(`Personnel Notified: ${polishedOrMissing(obs.personnelNotified)}`);
  push(`Rough Notes: ${polishedOrMissing(obs.roughNotes)}`);
  push("");
  push("Observations are recorded as entered. This tool does not assign blame, does not invent root cause, and uses 'under review' wording when the user indicates the cause is unknown.");

  // 15. Witness / Review / Approval Placeholders
  sec(lines, "Witness / Review Placeholders");
  push(`BOP Technician: ${orMissing(witness.bopTech)}`);
  push(`Toolpusher: ${orMissing(witness.toolpusher)}`);
  push(`Rig Manager: ${orMissing(witness.rigManager)}`);
  push(`Company Representative: ${orMissing(witness.companyRep)}`);
  push(`Client Representative: ${orMissing(witness.clientRep)}`);
  push(`Third-Party Witness: ${orMissing(witness.thirdParty)}`);
  push(`Date / Time Reviewed: ${orMissing(witness.reviewedAt)}`);
  push(`Comments: ${polishedOrMissing(witness.comments)}`);
  push("");
  push("Witness / review entries are placeholders only. Signatures and approval are recorded outside this tool. Approval is not implied unless the user records reviewed / approved status.");

  if (notes.roughNotes) {
    sec(lines, "Additional Rough Notes");
    push(polishedOrMissing(notes.roughNotes));
  }

  // Special warning
  sec(lines, "Surface BOP Test Report Special Warning");
  push("Draft Surface BOP Test Report support only. This tool records user-entered test information and does not certify BOP condition, component integrity, closing-unit reliability, well safety, regulatory compliance, or operational acceptance. All surface BOP test results must be verified, interpreted, witnessed, and approved by competent personnel under approved procedures, manufacturer requirements, client requirements, and applicable regulations.");

  return lines.join("\n");
}

export const surfaceBopChecklist = (s) => {
  const out = [];
  const h = s.header || {}, stack = s.stack || {}, scope = s.scope || {}, summary = s.summary || {}, witness = s.witness || {};
  if (!h.reportDate) out.push("Report date missing");
  if (!h.rigName) out.push("Rig name missing");
  if (!h.wellName) out.push("Well name missing");
  if (!h.procedureRef) out.push("Test procedure reference missing");
  if (!stack.configuration) out.push("Stack configuration missing");
  if (!scope.startTime) out.push("Test start time missing");
  if (!scope.endTime) out.push("Test end time missing");
  if (!witness.bopTech && !witness.companyRep && !witness.clientRep && !witness.toolpusher) out.push("Witness / review fields missing");
  const components = Array.isArray(s.components) ? s.components : [];
  if (!components.length) out.push("Component status fields missing — add at least one component row");
  if (!summary.chartAttached) out.push("Pressure chart reference missing");
  if (!summary.finalStatus) out.push("Final user-entered status missing");
  return out;
};

export const SURFACE_BOP_QUICK_PHRASES = [
  "Test performed as per approved procedure.",
  "Recorded status only.",
  "Pressure held as recorded.",
  "Pressure drop observed as recorded.",
  "Leak observed as recorded.",
  "No leak observed as recorded.",
  "Retest required as recorded.",
  "Retest completed as recorded.",
  "Results pending supervisor review.",
  "BOP technician notified.",
  "Company representative witnessed.",
  "Chart reference entered.",
  "Follow-up required.",
  "Repairs pending.",
  "No interpretation entered.",
];

export const SURFACE_BOP_COMPONENT_TYPES = [
  "Annular preventer",
  "Upper pipe ram",
  "Middle pipe ram",
  "Lower pipe ram",
  "Blind ram",
  "Blind shear ram",
  "Casing shear ram",
  "Variable bore ram",
  "Drilling spool",
  "Mud cross",
  "Choke valve",
  "Kill valve",
  "HCR valve",
  "Manual valve",
  "Other",
];

export const SURFACE_BOP_TEST_TYPES = [
  "Full surface BOP pressure test",
  "Function test",
  "Component test",
  "Post-maintenance test",
  "Regulatory test",
  "Client-required test",
  "Other",
];
